import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import EmployeeAvatar from "../../../../../components/EmployeeAvatar";
import {
  fetchMessages,
  fetchUnreadCount,
  fetchChatAttachments,
  sendMessage as sendChatMessage,
  uploadChatFiles,
  clearError,
  clearUploadError,
} from "../../../../../core/data/redux/nocOutsideJobChatSlice";
import {
  openChatAttachment,
  openChatAttachmentInExternalView,
  downloadChatAttachment,
  prepareUploadedAttachments,
  mapMessageAttachments,
  formatChatMessageText,
} from "../../../../../utils/nocChatUtils";

interface NocChatSectionProps {
  nocData: any;
  nocType: string;
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const NocChatSection: React.FC<NocChatSectionProps> = ({
  nocData,
  nocType,
  onShowToast,
}) => {
  const dispatch = useDispatch<any>();
  const [showChat, setShowChat] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Redux state from 'nocChat' slice (registered in store.tsx as nocChat)
  const {
    messages = [],
    unreadCount = 0,
    loading: chatLoading = false,
    error: chatError = null,
    uploadError = null,
    attachments = [],
  } = useSelector((state: any) => state.nocChat || {});

  const user = useSelector(
    (state: any) => state.employeeAuthState?.user || state.authState?.user
  );

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    if (showChat) {
      scrollToBottom();
    }
  }, [messages, showChat]);

  // Initial fetch when nocData is available (NO recurring interval polling)
  useEffect(() => {
    if (nocData?.id) {
      dispatch(fetchUnreadCount(nocData.id));
      dispatch(fetchMessages({ nocRequestId: nocData.id }));
    }
  }, [nocData?.id, dispatch]);

  const handleChatToggle = () => {
    const nextState = !showChat;
    setShowChat(nextState);
    if (nextState && nocData?.id) {
      dispatch(fetchMessages({ nocRequestId: nocData.id }));
      dispatch(fetchChatAttachments({ nocRequestId: nocData.id, nocType }));
      setTimeout(() => scrollToBottom("auto"), 200);
    } else {
      setSelectedFiles([]);
      setShowDocTypeModal(false);
    }
  };

  const handleManualSync = () => {
    if (nocData?.id) {
      dispatch(fetchMessages({ nocRequestId: nocData.id }));
      dispatch(fetchChatAttachments({ nocRequestId: nocData.id, nocType }));
      onShowToast("Info", "Conversation synchronized", "primary");
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const distFromBottom = scrollHeight - scrollTop - clientHeight;
    setShowScrollDown(distFromBottom > 100);
  };

  const isBoilerplateDocMessage = (text: string) => {
    if (!text) return false;
    const lower = text.trim().toLowerCase();
    return (
      lower === "please share education documents" ||
      lower === "please share advertisement documents" ||
      lower === "please share other documents" ||
      (lower.startsWith("please share") && lower.endsWith("documents"))
    );
  };

  // File selection validation
  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const errors: string[] = [];
      const allowedExts = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "webp"];

      fileArray.forEach((file: File) => {
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!allowedExts.includes(ext)) {
          errors.push(`${file.name}: File type not allowed.`);
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          errors.push(`${file.name}: Exceeds 10MB limit.`);
          return;
        }
        validFiles.push(file);
      });

      if (errors.length > 0) {
        onShowToast("Warning", errors.join(" "), "warning");
      }
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
        // Mobile parity: After selecting an attachment, do not retain boilerplate document request text
        // like "Please share Education Documents". Reset messageText so user can type their real message.
        if (isBoilerplateDocMessage(messageText)) {
          setMessageText("");
        }
        setTimeout(() => messageInputRef.current?.focus(), 200);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => {
      const remaining = prev.filter((_, idx) => idx !== index);
      if (remaining.length === 0) {
        setInputError(null);
      }
      return remaining;
    });
  };

  // Determine if a message was sent by the current user
  const isMyMessage = (msg: any): boolean => {
    const userStr = localStorage.getItem("user");
    const userObj = userStr && userStr !== "undefined" ? JSON.parse(userStr) : {};
    const myEmpId = String(userObj?.empId || userObj?.employeeId || user?.empId || user?.employeeId || "")
      .trim()
      .replace(/^0+/, "");
    const senderEmpId = String(msg.senderEmpId || msg.senderId || msg.empID || "")
      .trim()
      .replace(/^0+/, "");

    if (myEmpId && senderEmpId) {
      return myEmpId === senderEmpId;
    }
    const isApprover =
      userObj?.role === "APPROVER" ||
      userObj?.user_type === "APPROVER" ||
      Boolean(userObj?.approver_level);
    if (isApprover) {
      return msg.senderType === "approver" || msg.senderType === "admin";
    }
    return msg.senderType === "employee";
  };

  // Send message - supports text-only or text+files combined (mobile parity)
  const handleSendMessage = async () => {
    const trimmedMsg = messageText.trim();
    const isBoilerplate = isBoilerplateDocMessage(trimmedMsg);

    // If both message and files and doc type are empty, nothing to send
    if (!trimmedMsg && selectedFiles.length === 0 && !selectedDocumentType) return;

    // Mobile parity (lines 669-674 in mobile StartChat.tsx):
    // If attachment(s) are present but no message text, show friendly error and keep attachment in place
    if (selectedFiles.length > 0 && (!trimmedMsg || isBoilerplate)) {
      if (isBoilerplate) {
        setMessageText("");
      }
      const errorMsg = "Please add a message along with the attachment.";
      setInputError(errorMsg);
      toast.warning(errorMsg);
      onShowToast("Warning", errorMsg, "warning");
      setTimeout(() => messageInputRef.current?.focus(), 100);
      return;
    }

    if (!nocData?.id) {
      onShowToast("Error", "NOC Application ID is missing", "danger");
      return;
    }

    try {
      setIsSending(true);
      let uploadedDocs: any[] = [];

      // Upload selected files if any (base64 to AWS bucket, matching backend contract)
      if (selectedFiles.length > 0) {
        const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
        if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
          uploadedDocs = prepareUploadedAttachments(
            uploadResult.payload,
            selectedFiles,
            selectedDocumentType || undefined
          );
        } else {
          onShowToast("Error", "Failed to upload attached files. Please try again.", "danger");
          setIsSending(false);
          return;
        }
      }

      // Format message text with embedded attachment reference for backend @IsNotEmpty validator
      // Never send boilerplate share messages as the payload message with attachments
      let textToEmbed = isBoilerplate ? "" : trimmedMsg;
      if (!textToEmbed && selectedFiles.length === 0 && selectedDocumentType) {
        textToEmbed = `Please share ${selectedDocumentType} Documents`;
      }
      const messageForBackend = formatChatMessageText(textToEmbed, uploadedDocs);

      const payload: any = {
        nocRequestId: Number(nocData.id) || nocData.id,
        message: messageForBackend,
        attachments: uploadedDocs,
        documents: uploadedDocs,
        files: selectedFiles,
      };

      if (selectedDocumentType) {
        payload.documentType = selectedDocumentType;
      }

      await dispatch(sendChatMessage(payload)).unwrap();

      // Reset input fields
      setMessageText("");
      setSelectedFiles([]);
      setSelectedDocumentType("");
      setInputError(null);
      setShowDocTypeModal(false);

      // Refresh conversation once
      dispatch(fetchMessages({ nocRequestId: nocData.id }));
      if (uploadedDocs.length > 0) {
        dispatch(fetchChatAttachments({ nocRequestId: nocData.id, nocType }));
      }
      onShowToast("Success", "Message sent successfully", "success");
    } catch (err: any) {
      const errMsg =
        (typeof err === "string" && err.trim()) ||
        err?.message ||
        err?.error ||
        (Array.isArray(err?.data) && err.data[0]) ||
        "Failed to send message";
      onShowToast("Error", errMsg, "danger");
    } finally {
      setIsSending(false);
    }
  };

  // Enter sends, Shift + Enter creates a new line
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageTime = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? ""
      : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Group messages by date matching mobile
  const getGroupedMessages = () => {
    if (!messages || messages.length === 0) return [];
    const grouped: any[] = [];
    let lastDate = "";

    messages.forEach((msg: any, index: number) => {
      const msgDate = msg.createdAt ? new Date(msg.createdAt) : new Date();
      const dateStr = msgDate.toDateString();
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();

      let displayDate = dateStr;
      if (dateStr === today) displayDate = "Today";
      else if (dateStr === yesterday) displayDate = "Yesterday";
      else {
        displayDate = msgDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      if (displayDate !== lastDate) {
        grouped.push({ type: "date", date: displayDate, id: `date-${index}` });
        lastDate = displayDate;
      }
      grouped.push({ ...msg, type: "message" });
    });

    return grouped;
  };

  const renderAttachmentItem = (att: any, aIdx: number) => {
    const rawFileName = att.fileName || att.FileName || att.name || "Attachment";
    const cleanFileName = (rawFileName.split("/").pop() || rawFileName).replace(/\s*\([^)]*\)\s*$/, "").trim();
    const ext = (att.fileExtension || att.FileExtension || cleanFileName.split(".").pop() || "").toLowerCase();
    const isPdf = ext === "pdf";
    const isImg = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

    return (
      <div
        key={aIdx}
        className="d-flex align-items-center justify-content-between p-2 mt-2 cursor-pointer attachment-item-hover"
        style={{
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          transition: "all 0.15s ease",
        }}
        onClick={() => openChatAttachment(att, nocData?.id, nocType, dispatch, attachments)}
        title="Tap to open attachment"
      >
        <div className="d-flex align-items-center gap-2.5 overflow-hidden me-2">
          {/* Minimalist soft icon badge */}
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "6px",
              backgroundColor: isPdf ? "#fee2e2" : isImg ? "#eff6ff" : "#f1f5f9",
              color: isPdf ? "#dc2626" : isImg ? "#2563eb" : "#475569",
            }}
          >
            <i className={`ti ${isPdf ? "ti-file-text" : isImg ? "ti-photo" : "ti-file"} fs-16`}></i>
          </div>
          <div className="overflow-hidden">
            <div
              className="text-truncate fw-medium text-dark"
              style={{ fontSize: "12.5px", maxWidth: "220px", lineHeight: 1.3 }}
              title={cleanFileName}
            >
              {cleanFileName}
            </div>
            <small className="text-muted d-flex align-items-center gap-1.5 flex-wrap" style={{ fontSize: "10.5px", marginTop: "1px" }}>
              {att.documentType && att.documentType !== "Upload Documents" && (
                <span
                  className="badge px-1.5 py-0.5 fw-medium"
                  style={{
                    backgroundColor: "#fef2f2",
                    color: "#b91c1c",
                    border: "1px solid #fee2e2",
                    fontSize: "9.5px",
                    borderRadius: "4px",
                  }}
                >
                  <i className="ti ti-file-text me-1"></i>
                  {att.documentType}
                </span>
              )}
              <span>{ext ? ext.toUpperCase() : "FILE"} • Tap to open</span>
            </small>
          </div>
        </div>
        <div className="d-flex align-items-center gap-1.5 flex-shrink-0 text-muted pe-1">
          <i className="ti ti-chevron-right fs-14 text-muted"></i>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Communication Card in Page Body */}
      <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: "4px" }}>
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-1 text-dark fw-bold">
                <i className="ti ti-messages text-primary me-2"></i>
                Communication
              </h5>
              <p className="text-muted small mb-0">Chat with applicant or other approvers regarding this NOC</p>
            </div>
            <button
              className="btn btn-primary btn-sm position-relative d-flex align-items-center gap-1.5"
              style={{ borderRadius: "4px" }}
              onClick={handleChatToggle}
            >
              <i className="ti ti-message fs-14"></i>
              <span>{showChat ? "Close Chat" : "Start Chat"}</span>
              {unreadCount > 0 && (
                <span className="badge bg-danger ms-1" style={{ borderRadius: "3px" }}>
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Full Screen Right Side Panel */}
      {showChat && (
        <>
          {/* Backdrop overlay */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{
              zIndex: 99990,
              backgroundColor: "rgba(15, 23, 42, 0.35)",
              backdropFilter: "blur(2px)",
              transition: "opacity 0.2s ease",
            }}
            onClick={handleChatToggle}
          />

          {/* Right Side Panel */}
          <div
            className="position-fixed top-0 end-0 h-100 bg-white d-flex flex-column noc-side-panel"
            style={{
              width: isFullScreen ? "100vw" : "480px",
              maxWidth: "100vw",
              zIndex: 99999,
              borderLeft: "1px solid #e2e8f0",
              boxShadow: "-4px 0 24px rgba(15, 23, 42, 0.08)",
              overflow: "hidden",
              transition: "width 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* Right Side Panel Header */}
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2.5 border-bottom bg-white flex-shrink-0"
              style={{ borderColor: "#f1f5f9" }}
            >
              <div className="d-flex align-items-center gap-2.5">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#0f172a",
                  }}
                >
                  <i className="ti ti-messages fs-17 text-dark"></i>
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <h6 className="mb-0 fw-semibold text-dark fs-14" style={{ letterSpacing: "-0.2px" }}>
                      NOC Messages
                    </h6>
                    <span
                      className="d-inline-flex align-items-center gap-1 px-1.5 py-0.5 rounded-pill"
                      style={{ background: "#ecfdf5", color: "#059669", fontSize: "10.5px", fontWeight: 500 }}
                    >
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981" }}></span>
                      Live
                    </span>
                  </div>
                  <div className="text-muted" style={{ fontSize: "11.5px", marginTop: "1px" }}>
                    Ref: #{nocData?.referenceNumber || nocData?.id || ""}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-1">
                <button
                  className="btn btn-sm p-0 d-flex align-items-center justify-content-center text-muted header-action-btn"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "6px",
                    border: "none",
                    background: "transparent",
                  }}
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  title={isFullScreen ? "Restore to Side Panel" : "Expand to Full Screen"}
                >
                  <i className={`ti ${isFullScreen ? "ti-arrows-minimize" : "ti-arrows-maximize"} fs-15`}></i>
                </button>
                <button
                  className="btn btn-sm p-0 d-flex align-items-center justify-content-center text-muted header-action-btn"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "6px",
                    border: "none",
                    background: "transparent",
                  }}
                  onClick={handleManualSync}
                  disabled={chatLoading}
                  title="Sync Conversation"
                >
                  <i className={`ti ti-refresh fs-14 ${chatLoading ? "ti-spin text-primary" : ""}`}></i>
                </button>
                <button
                  className="btn btn-sm p-0 d-flex align-items-center justify-content-center text-muted header-action-btn"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "6px",
                    border: "none",
                    background: "transparent",
                  }}
                  onClick={handleChatToggle}
                  title="Close Chat"
                >
                  <i className="ti ti-x fs-17"></i>
                </button>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="p-3 flex-grow-1 overflow-auto position-relative"
              style={{ backgroundColor: "#f8fafc" }}
            >
              {(chatError || uploadError) && (
                <div
                  className="alert alert-danger py-2 px-3 fs-12 mb-3 border shadow-xs d-flex align-items-center justify-content-between"
                  style={{ borderRadius: "4px" }}
                >
                  <span>{chatError || uploadError}</span>
                  <button
                    type="button"
                    className="btn-close fs-10"
                    onClick={() => {
                      dispatch(clearError());
                      dispatch(clearUploadError());
                    }}
                  ></button>
                </div>
              )}

              {chatLoading && messages.length === 0 ? (
                <div className="text-center py-5">
                  <div className="spinner-border spinner-border-sm text-danger" role="status"></div>
                  <p className="text-muted small mt-2 fs-12">Loading conversation...</p>
                </div>
              ) : messages.length === 0 ? (
                <div
                  className="d-flex flex-column align-items-center justify-content-center py-5 my-auto text-center"
                  style={{ minHeight: "280px" }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "50%",
                      backgroundColor: "#f8fafc",
                      border: "1px solid #f1f5f9",
                      color: "#94a3b8",
                    }}
                  >
                    <i className="ti ti-messages fs-36" style={{ opacity: 0.75 }}></i>
                  </div>
                  <h6 className="fw-semibold mb-1" style={{ color: "#64748b", fontSize: "15px" }}>
                    No messages yet
                  </h6>
                  <p className="small mb-0" style={{ color: "#94a3b8", fontSize: "12.5px" }}>
                    Start the conversation below
                  </p>
                </div>
              ) : (
                getGroupedMessages().map((item: any, idx: number) => {
                  if (item.type === "date") {
                    return (
                      <div key={item.id || idx} className="d-flex align-items-center my-3 text-muted">
                        <div className="flex-grow-1" style={{ height: "1px", backgroundColor: "#e2e8f0" }}></div>
                        <span
                          className="px-2.5 text-secondary fw-medium"
                          style={{ fontSize: "11px", letterSpacing: "0.2px" }}
                        >
                          {item.date}
                        </span>
                        <div className="flex-grow-1" style={{ height: "1px", backgroundColor: "#e2e8f0" }}></div>
                      </div>
                    );
                  }

                  const msg = item;
                  const isOwn = isMyMessage(msg);
                  const rawMsg = msg.message || "";
                  const paperclipIdx = rawMsg.indexOf("📎");
                  const hasPaperclip = paperclipIdx !== -1;
                  const displayMsgText = hasPaperclip
                    ? rawMsg.substring(0, paperclipIdx).trim()
                    : rawMsg.trim();

                  let extractedAttNames: string[] = [];
                  if (hasPaperclip) {
                    const attPart = rawMsg.substring(paperclipIdx).replace(/^[📎\s]+/, "").trim();
                    extractedAttNames = attPart.split(",").map((s: string) => s.trim()).filter(Boolean);
                  }

                  const mappedMsg = mapMessageAttachments(msg, attachments);
                  const attachmentsList: any[] =
                    mappedMsg?.attachments && mappedMsg.attachments.length > 0
                      ? mappedMsg.attachments
                      : extractedAttNames.length > 0
                        ? extractedAttNames.map((name) => ({ fileName: name, name }))
                        : [];

                  return (
                    <div
                      key={msg.id || idx}
                      className={`d-flex mb-3 ${isOwn ? "justify-content-end" : "justify-content-start"}`}
                    >
                      {!isOwn && (
                        <div className="me-2 flex-shrink-0 align-self-end mb-1">
                          <EmployeeAvatar
                            empName={msg.senderName || "Participant"}
                            empId={msg.senderEmpId}
                            size="sm"
                            showName={false}
                            showEmpId={false}
                            className="rounded-circle"
                          />
                        </div>
                      )}

                      <div style={{ maxWidth: "82%", wordBreak: "break-word" }}>
                        {!isOwn && (
                          <div className="d-flex align-items-center gap-1.5 mb-1 px-1">
                            <span className="fw-semibold text-dark" style={{ fontSize: "11.5px" }}>
                              {msg.senderName || "Participant"}
                            </span>
                            <span
                              className="badge bg-light text-muted border px-1.5 py-0.5 text-capitalize"
                              style={{ borderRadius: "4px", fontSize: "9.5px", borderColor: "#e2e8f0" }}
                            >
                              {msg.senderType || "User"}
                            </span>
                          </div>
                        )}

                        {/* Minimalist Standardized Bubble */}
                        <div
                          className="p-3 bg-white border"
                          style={{
                            borderRadius: isOwn ? "14px 14px 2px 14px" : "14px 14px 14px 2px",
                            borderColor: "#e2e8f0",
                            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                          }}
                        >
                          {/* Document type tag if tagged */}
                          {msg.documentType && (
                            <div className="mb-2">
                              <span
                                className="d-inline-flex align-items-center gap-1 px-2 py-0.5"
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 500,
                                  color: "#b91c1c",
                                  backgroundColor: "#fef2f2",
                                  borderRadius: "4px",
                                  border: "1px solid #fee2e2",
                                }}
                              >
                                <i className="ti ti-file-text fs-12"></i>
                                {msg.documentType}
                              </span>
                            </div>
                          )}

                          {/* Text message */}
                          {displayMsgText ? (
                            <div
                              className="text-dark"
                              style={{
                                fontSize: "13.5px",
                                whiteSpace: "pre-wrap",
                                lineHeight: 1.5,
                                fontWeight: 400,
                                color: "#1e293b",
                              }}
                            >
                              {displayMsgText}
                            </div>
                          ) : null}

                          {/* Attachments List */}
                          {attachmentsList.length > 0 && (
                            <div>
                              {attachmentsList.map((att: any, aIdx: number) =>
                                renderAttachmentItem(att, aIdx)
                              )}
                            </div>
                          )}

                          {/* Time & Read Status */}
                          <div className="d-flex align-items-center justify-content-end gap-1 mt-1.5 text-muted">
                            <span style={{ fontSize: "10.5px", color: "#94a3b8" }}>
                              {formatMessageTime(msg.createdAt)}
                            </span>
                            {isOwn && (
                              <i className="ti ti-checks fs-13" style={{ color: "#3b82f6" }}></i>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              <div ref={messagesEndRef} />

              {/* Scroll-to-bottom button */}
              {showScrollDown && (
                <button
                  className="btn btn-sm shadow position-sticky float-end text-white"
                  style={{
                    bottom: "10px",
                    right: "10px",
                    width: "28px",
                    height: "28px",
                    padding: 0,
                    backgroundColor: "#0f172a",
                    borderRadius: "50%",
                    zIndex: 5,
                  }}
                  onClick={() => scrollToBottom()}
                  title="Scroll to bottom"
                >
                  <i className="ti ti-chevron-down fs-13"></i>
                </button>
              )}
            </div>

            {/* Staging Area for Selected Files: Minimalist Preview Card */}
            {selectedFiles.length > 0 && (
              <div className="px-3 pt-2 pb-1 bg-white border-top flex-shrink-0" style={{ borderColor: "#f1f5f9" }}>
                <div className="d-flex flex-column gap-1.5">
                  {selectedFiles.map((f, i) => {
                    const ext = (f.name.split(".").pop() || "").toLowerCase();
                    const isPdf = ext === "pdf";
                    const isImg = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);
                    const fileSizeKB = Math.round(f.size / 1024);

                    return (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between p-2 bg-light border"
                        style={{
                          borderRadius: "8px",
                          borderColor: "#e2e8f0",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const blobUrl = URL.createObjectURL(f);
                          window.open(blobUrl, "_blank");
                        }}
                        title="Tap to preview"
                      >
                        <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
                          {/* Icon badge */}
                          <div
                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: "30px",
                              height: "30px",
                              borderRadius: "6px",
                              backgroundColor: isPdf ? "#fee2e2" : isImg ? "#eff6ff" : "#f1f5f9",
                              color: isPdf ? "#dc2626" : isImg ? "#2563eb" : "#475569",
                            }}
                          >
                            <i
                              className={`ti ${isPdf ? "ti-file-text" : isImg ? "ti-photo" : "ti-file"
                                } fs-15`}
                            ></i>
                          </div>

                          {/* File info */}
                          <div className="overflow-hidden">
                            <div
                              className="text-truncate fw-medium text-dark"
                              style={{ fontSize: "12.5px", maxWidth: "260px", lineHeight: 1.25 }}
                              title={f.name}
                            >
                              {f.name}
                            </div>
                            <small
                              className="text-muted d-flex align-items-center gap-1.5 flex-wrap"
                              style={{ fontSize: "10.5px", marginTop: "1px" }}
                            >
                              {selectedDocumentType && (
                                <span
                                  className="badge px-1.5 py-0.5 fw-medium"
                                  style={{
                                    backgroundColor: "#fef2f2",
                                    color: "#b91c1c",
                                    border: "1px solid #fee2e2",
                                    fontSize: "9.5px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <i className="ti ti-file-text me-1"></i>
                                  {selectedDocumentType}
                                </span>
                              )}
                              <span>{ext.toUpperCase()} • {fileSizeKB}KB • Ready to send</span>
                            </small>
                          </div>
                        </div>

                        {/* Delete button */}
                        <button
                          type="button"
                          className="btn p-0 flex-shrink-0 d-flex align-items-center justify-content-center text-muted"
                          style={{
                            width: "22px",
                            height: "22px",
                            borderRadius: "50%",
                            backgroundColor: "#e2e8f0",
                            border: "none",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSelectedFile(i);
                          }}
                          title="Remove file"
                        >
                          <i className="ti ti-x fs-12"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Active Document Type Tag if Approver Tagged One */}
            {selectedDocumentType && (
              <div className="px-3 pt-1 pb-1 flex-shrink-0">
                <span
                  className="badge bg-white text-dark border d-inline-flex align-items-center gap-1.5 fs-11 fw-normal px-2.5 py-1"
                  style={{ borderRadius: "16px", borderColor: "#e2e8f0" }}
                >
                  <i className="ti ti-file-text text-danger fs-12"></i>
                  <span>Document: <strong>{selectedDocumentType}</strong></span>
                  <button
                    type="button"
                    className="btn btn-link text-muted p-0 ms-1 d-flex align-items-center"
                    style={{ textDecoration: "none" }}
                    onClick={() => setSelectedDocumentType("")}
                    title="Clear document type tag"
                  >
                    <i className="ti ti-x fs-10"></i>
                  </button>
                </span>
              </div>
            )}

            {/* Input Error Message Banner (Direct Attachment Requires Message) */}
            {inputError && (
              <div className="px-3 pt-2 pb-1 flex-shrink-0">
                <div
                  className="d-flex align-items-center justify-content-between p-2 px-2.5 rounded-3 border"
                  style={{
                    backgroundColor: "#fef2f2",
                    borderColor: "#fecaca",
                    color: "#b91c1c",
                    fontSize: "12.5px",
                    fontWeight: 500,
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className="ti ti-alert-circle fs-16 flex-shrink-0" style={{ color: "#dc2626" }}></i>
                    <span>{inputError}</span>
                  </div>
                  <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent text-danger d-flex align-items-center ms-2"
                    onClick={() => setInputError(null)}
                    title="Dismiss"
                  >
                    <i className="ti ti-x fs-12"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Hidden File Input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.webp"
              onChange={handleFileSelection}
            />

            {/* Minimalist Standardized Input Bar */}
            <div className="p-3 bg-white border-top flex-shrink-0" style={{ borderColor: "#f1f5f9" }}>
              <div
                className="d-flex align-items-center border px-2.5 py-1.5 chat-input-bar"
                style={{
                  borderRadius: "24px",
                  borderColor: inputError ? "#f87171" : "#e2e8f0",
                  minHeight: "44px",
                  backgroundColor: "#f8fafc",
                  transition: "all 0.15s ease",
                }}
              >
                {/* Paperclip Button */}
                <button
                  type="button"
                  className="btn p-0 text-muted d-flex align-items-center justify-content-center me-1 flex-shrink-0 chat-icon-btn"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    border: "none",
                    background: "transparent",
                    color: "#64748b",
                  }}
                  onClick={() => {
                    setShowDocTypeModal(true);
                  }}
                  title="Attach files or request document type"
                >
                  <i className="ti ti-paperclip fs-18"></i>
                </button>

                {/* Text input */}
                <input
                  ref={messageInputRef}
                  type="text"
                  className="form-control border-0 bg-transparent shadow-none px-2 py-1 flex-grow-1"
                  placeholder="Type a message..."
                  value={messageText}
                  onChange={(e) => {
                    setMessageText(e.target.value);
                    if (inputError) setInputError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isSending}
                  style={{
                    fontSize: "13.5px",
                    color: "#0f172a",
                  }}
                />

                {/* Minimalist Send Button */}
                <button
                  type="button"
                  className="btn p-0 d-flex align-items-center justify-content-center ms-1 flex-shrink-0"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor:
                      messageText.trim() || selectedFiles.length > 0 || selectedDocumentType ? "#0f172a" : "#e2e8f0",
                    color:
                      messageText.trim() || selectedFiles.length > 0 || selectedDocumentType ? "#ffffff" : "#94a3b8",
                    border: "none",
                    cursor:
                      messageText.trim() || selectedFiles.length > 0 || selectedDocumentType ? "pointer" : "default",
                    transition: "all 0.2s ease",
                  }}
                  onClick={handleSendMessage}
                  disabled={isSending || (!messageText.trim() && selectedFiles.length === 0 && !selectedDocumentType)}
                  title="Send"
                >
                  {isSending ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                      style={{ width: "13px", height: "13px", borderWidth: "1.5px" }}
                    ></div>
                  ) : (
                    <i className="ti ti-arrow-up fs-15" style={{ fontWeight: 600 }}></i>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile-identical Modal: Select Document Type (Screenshot 1) */}
            {showDocTypeModal && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
                style={{
                  backgroundColor: "rgba(15, 23, 42, 0.45)",
                  zIndex: 1090,
                  backdropFilter: "blur(3px)",
                }}
                onClick={() => setShowDocTypeModal(false)}
              >
                <div
                  className="bg-white shadow-xl p-4 w-100 position-relative"
                  style={{
                    maxWidth: "380px",
                    borderRadius: "20px",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h5 className="fw-bold text-dark mb-1" style={{ fontSize: "18px" }}>
                    Select Document Type
                  </h5>
                  <p className="text-muted fs-12 mb-3" style={{ color: "#94a3b8" }}>
                    Which type of document are you requesting from the employee?
                  </p>

                  <div className="d-flex flex-column gap-2 mb-1">
                    {/* Education Documents */}
                    <div
                      className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                      style={{
                        borderRadius: "14px",
                        transition: "background-color 0.15s ease",
                        backgroundColor: "#ffffff",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                      onClick={() => {
                        setSelectedDocumentType("Education");
                        setMessageText("Please share education document");
                        setInputError(null);
                        setShowDocTypeModal(false);
                        setTimeout(() => messageInputRef.current?.focus(), 150);
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            backgroundColor: "#f1f5f9",
                          }}
                        >
                          📚
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                            Education Documents
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                            Certificates, degrees, transcripts, etc.
                          </small>
                        </div>
                      </div>
                      <i className="ti ti-chevron-right text-muted fs-15"></i>
                    </div>

                    {/* Advertisement Documents */}
                    <div
                      className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                      style={{
                        borderRadius: "14px",
                        transition: "background-color 0.15s ease",
                        backgroundColor: "#ffffff",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                      onClick={() => {
                        setSelectedDocumentType("Advertisement");
                        setMessageText("Please share advertisement document");
                        setInputError(null);
                        setShowDocTypeModal(false);
                        setTimeout(() => messageInputRef.current?.focus(), 150);
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            backgroundColor: "#f1f5f9",
                          }}
                        >
                          📢
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                            Advertisement Documents
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                            Job posting, newspaper clipping etc.
                          </small>
                        </div>
                      </div>
                      <i className="ti ti-chevron-right text-muted fs-15"></i>
                    </div>

                    {/* Other Documents */}
                    <div
                      className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                      style={{
                        borderRadius: "14px",
                        transition: "background-color 0.15s ease",
                        backgroundColor: "#ffffff",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                      onClick={() => {
                        setSelectedDocumentType("Other");
                        setMessageText("Please share other document");
                        setInputError(null);
                        setShowDocTypeModal(false);
                        setTimeout(() => messageInputRef.current?.focus(), 150);
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            backgroundColor: "#f1f5f9",
                          }}
                        >
                          📄
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                            Other Documents
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                            Any other supporting documents.
                          </small>
                        </div>
                      </div>
                      <i className="ti ti-chevron-right text-muted fs-15"></i>
                    </div>

                    {/* Horizontal Divider */}
                    <div style={{ height: "1px", backgroundColor: "#f1f5f9", margin: "6px 0" }}></div>

                    {/* Send Attachment */}
                    <div
                      className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                      style={{
                        borderRadius: "14px",
                        transition: "background-color 0.15s ease",
                        backgroundColor: "#ffffff",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(200, 35, 51, 0.04)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                      onClick={() => {
                        setSelectedDocumentType("");
                        setInputError(null);
                        setShowDocTypeModal(false);
                        setTimeout(() => fileInputRef.current?.click(), 150);
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            backgroundColor: "rgba(200, 35, 51, 0.1)",
                            color: "#c82333",
                          }}
                        >
                          <i className="ti ti-paperclip" style={{ color: "#c82333" }}></i>
                        </div>
                        <div>
                          <div className="fw-bold" style={{ fontSize: "14px", color: "#c82333" }}>
                            Send Attachment
                          </div>
                          <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                            Supported: PDF, JPG, PNG
                          </small>
                        </div>
                      </div>
                      <i className="ti ti-chevron-right fs-15 mr-2" style={{ color: "#c82333" }}></i>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <style>
              {`
              .header-action-btn:hover {
                background-color: #f1f5f9 !important;
                color: #0f172a !important;
              }
              .attachment-item-hover:hover {
                background-color: #f1f5f9 !important;
                border-color: #cbd5e1 !important;
              }
              .chat-input-bar:focus-within {
                background-color: #ffffff !important;
                border-color: #cbd5e1 !important;
                box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.05) !important;
              }
              .chat-icon-btn:hover {
                background-color: #e2e8f0 !important;
                color: #0f172a !important;
              }
              @keyframes sidePanelSlideIn {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
              .noc-side-panel {
                animation: sidePanelSlideIn 0.22s cubic-bezier(0.16, 1, 0.3, 1);
              }
            `}
            </style>
          </div>
        </>
      )}
    </>
  );
};


